import React from 'react'
import classNames from 'classnames'

import { IHeaderNavigationSpotlight, getUrlWithPageviewParam } from '..'
import useLocaleCode from 'marketing-site/src/library/hooks/useLocaleCode'
import Image from 'marketing-site/src/components/common/NextImageWrapper'
import { Text } from 'marketing-site/src/library/elements/Text'
import { Icon } from 'marketing-site/src/library/utils/icons'
import { CTALink } from 'marketing-site/src/library/elements/CTALink'
import { CTALink as CTALinkMVP } from 'marketing-site/src/mvp/library/elements/CTALink'
import { CTATheme, mq } from 'marketing-site/src/library/utils'
import { CTATheme as CTAThemeMVP } from 'marketing-site/src/mvp/library/utils'

export const HeaderNavigationSpotlight = ({
  contentType = 'flex',
  theme = 'light',
  title,
  description,
  media,
  url,
  linkText,
}: IHeaderNavigationSpotlight): React.ReactElement => {
  const localeCode = useLocaleCode()
  const imageSizes = {
    tablet: { width: 335, height: 200 },
    desktop: { width: 255, height: 152 },
  }

  const classes = classNames('header-navigation-spotlight', {
    [`header-navigation-spotlight--theme-${theme}`]: theme,
    compact: localeCode !== 'en-US',
  })

  return (
    <>
      <div
        className={classes}
        role="menu"
        aria-label={title}
        data-testid="header-navigation-spotlight"
      >
        <div className="header-navigation-spotlight__title">
          <Text
            brand={contentType === 'flex' ? 'default' : 'mvp'}
            size={contentType === 'flex' ? 'eyebrows' : 'eyebrow'}
          >
            {title}
          </Text>
        </div>
        <div className="header-navigation-spotlight__media">
          <Image
            src={media.url}
            width={335}
            height={200}
            layout={'responsive'}
            alt={media.altText}
            quality={100}
            sizes={`(${mq.desktop}) ${imageSizes.desktop.width}px, (${mq.tablet}) ${imageSizes.tablet.width}px, ${imageSizes.tablet.height}px`}
          />
        </div>
        <div className="header-navigation-spotlight__description">
          <Text brand={contentType === 'flex' ? 'default' : 'mvp'} size="body">
            {description}
          </Text>
        </div>
        <div className="header-navigation-spotlight__link">
          {contentType === 'flex' ? (
            <CTALink
              text={linkText}
              textSize="button-bold"
              url={getUrlWithPageviewParam(url)}
              bgColor={theme === 'light' ? CTATheme.LinkOnlyBlue : CTATheme.LinkOnlyWhite}
              arrow={true}
              icon={Icon.Arrow}
            />
          ) : (
            <CTALinkMVP
              variant={theme === 'light' ? CTAThemeMVP.LinkOnlyBlue : CTAThemeMVP.LinkOnlyPaper}
              url={getUrlWithPageviewParam(url)}
              text={linkText}
              textSize="button-bold"
              icon="Arrow"
            />
          )}
        </div>
      </div>

      <style jsx>
        {`
          .header-navigation-spotlight {
            display: flex;
            flex-direction: column;

            &__title {
              text-transform: uppercase;
            }

            &__media {
              margin-top: 1.25rem;
              border-radius: 1rem;
              overflow: hidden;
              max-width: 374px;

              :global(picture) {
                width: 100%;
                height: 0;
                padding-bottom: 60%;
                position: relative;
              }

              :global(img) {
                object-fit: cover;
                width: 100%;
                height: 100%;
                position: absolute;
                right: 0;
                top: 0;
                bottom: 0;
                left: 0;
              }
            }

            &__description {
              margin-top: 1rem;

              :global(span) {
                color: inherit;
              }
            }

            &__link {
              margin-top: 0.75rem;
            }

            &--theme-light {
              .header-navigation-spotlight__title,
              .header-navigation-spotlight__description {
                color: var(--neutral-800);
              }
            }

            &--theme-dark,
            &--theme-resolution-bot {
              .header-navigation-spotlight__title,
              .header-navigation-spotlight__description {
                color: var(--neutral-100);
              }
            }
          }

          @media (${mq.desktop}) {
            .header-navigation-spotlight {
              &__media {
                margin-top: 1.5rem;
                border-radius: 1.25rem;
              }

              &__link {
                margin-top: 1rem;
              }
            }
          }
        `}
      </style>
    </>
  )
}
